import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-6 py-8 sm:px-8 sm:py-10">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
              <p className="text-sm text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
            </div>

            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-4">
                  Coverdale Real Estate Buying & Selling Brokerage L.L.C ("we," "our," or "us") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                  or use our real estate services.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Contact Information:</h3>
                  <p className="text-blue-800 text-sm">
                    <strong>Company:</strong> Coverdale Real Estate Buying & Selling Brokerage L.L.C<br/>
                    <strong>Address:</strong> Office # 4108, Churchill Tower, Business Bay, Dubai, UAE<br/>
                    <strong>Email:</strong> info@covproperties.com
                  </p>
                </div>
              </section>

              {/* Information We Collect */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Personal Information</h3>
                <p className="text-gray-700 mb-4">
                  When you contact us or submit inquiries through our website, we may collect:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Property preferences and requirements</li>
                  <li>Any additional information you provide in contact forms</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Automatically Collected Information</h3>
                <p className="text-gray-700 mb-4">
                  We automatically collect certain information when you visit our website:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                  <li>IP address and location data</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website information</li>
                </ul>
              </section>

              {/* How We Use Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-700 mb-4">We use the collected information for the following purposes:</p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                  <li>To respond to your inquiries and provide real estate services</li>
                  <li>To educate and assist you in buying, selling, or investing in properties</li>
                  <li>To send you property updates, market information, and marketing communications</li>
                  <li>To improve our website and services</li>
                  <li>To comply with legal obligations</li>
                  <li>To maintain contact with you regarding ongoing or potential transactions</li>
                </ul>
                
                <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-yellow-900 mb-2">Marketing Communications:</h3>
                  <p className="text-yellow-800 text-sm">
                    By providing your contact information, you consent to receive updates about properties, market trends, 
                    and promotional materials. You can opt out of marketing communications at any time by emailing us at 
                    info@covproperties.com or using the unsubscribe option in our emails.
                  </p>
                </div>
              </section>

              {/* Cookies and Tracking */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Cookies and Tracking Technologies</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Essential Cookies</h3>
                <p className="text-gray-700 mb-4">
                  These cookies are necessary for the website to function properly and cannot be disabled.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Analytics Cookies</h3>
                <p className="text-gray-700 mb-4">
                  We use Google Tag Manager and Google Analytics to understand how visitors interact with our website. 
                  These cookies help us improve our services and user experience.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Marketing Cookies</h3>
                <p className="text-gray-700 mb-4">
                  We use Facebook Pixel and other marketing tools to deliver targeted advertisements and measure 
                  the effectiveness of our marketing campaigns.
                </p>

                <p className="text-gray-700 mb-4">
                  You can manage your cookie preferences through our cookie consent banner or by adjusting your browser settings.
                </p>
              </section>

              {/* Third-Party Services */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Third-Party Services</h2>
                <p className="text-gray-700 mb-4">We use the following third-party services:</p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                  <li><strong>Google Tag Manager & Analytics:</strong> Website analytics and performance tracking</li>
                  <li><strong>Facebook Pixel:</strong> Social media advertising and retargeting</li>
                  <li><strong>Resend:</strong> Email delivery and communication services</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  These services have their own privacy policies, and we encourage you to review them.
                </p>
              </section>

              {/* Property Information Disclaimer */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Property Information and Pricing Disclaimer</h2>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-red-900 mb-2">Important Notice:</h3>
                  <ul className="list-disc list-inside text-red-800 text-sm space-y-1">
                    <li>All property prices displayed are per developer's sales information and are subject to change without notice</li>
                    <li>Properties may be sold or become unavailable while you are waiting to be contacted</li>
                    <li>We are not responsible for price changes or property availability after initial inquiry</li>
                    <li>Our role as real estate professionals is to educate and assist clients in buying, selling, and investing smoothly</li>
                    <li>All property information should be verified directly with the developer or through official channels</li>
                    <li>Market conditions may affect pricing and availability at any time</li>
                  </ul>
                </div>
              </section>

              {/* Data Retention */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
                <p className="text-gray-700 mb-4">
                  We retain your personal information for a period of one (1) year from the date of last contact or 
                  interaction, unless:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                  <li>You request earlier deletion by emailing info@covproperties.com</li>
                  <li>We are required to retain information for legal or regulatory purposes</li>
                  <li>You have an ongoing business relationship with us</li>
                </ul>
              </section>

              {/* Your Rights */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Your Rights</h2>
                <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                  <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  To exercise any of these rights, please contact us at info@covproperties.com.
                </p>
              </section>

              {/* Data Security */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Data Security</h2>
                <p className="text-gray-700 mb-4">
                  We implement appropriate technical and organizational measures to protect your personal information 
                  against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
                  over the internet is 100% secure.
                </p>
              </section>

              {/* International Transfers */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
                <p className="text-gray-700 mb-4">
                  Our services are primarily focused on the UAE market. However, some of our third-party service 
                  providers may process data outside the UAE. We ensure appropriate safeguards are in place for 
                  any international data transfers.
                </p>
              </section>

              {/* Children's Privacy */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Children's Privacy</h2>
                <p className="text-gray-700 mb-4">
                  Our services are not intended for individuals under the age of 18. We do not knowingly collect 
                  personal information from children under 18.
                </p>
              </section>

              {/* Changes to Policy */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 mb-4">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                  the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </section>

              {/* Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800">
                    <strong>Coverdale Real Estate Buying & Selling Brokerage L.L.C</strong><br/>
                    Office # 4108, Churchill Tower<br/>
                    Business Bay, Dubai, UAE<br/>
                    Email: info@covproperties.com
                  </p>
                </div>
              </section>

              {/* Footer */}
              <div className="border-t pt-6 mt-8">
                <p className="text-sm text-gray-500 text-center">
                  This Privacy Policy is effective as of {new Date().toLocaleDateString()} and applies to all 
                  information collected by Coverdale Real Estate Buying & Selling Brokerage L.L.C
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 